<template>
  <ThemeProvider>
    <div class="main-wrapper no-before">
      <div
        class="flex wrapper"
        :style="{
          // height: userId ? `calc(100% - ${topHeight}px)` : '100%',
          height: `100%`,
        }"
      >
        <div v-if="userId" class="sider" :class="siderClass">
          <div class="relative h-full">
            <XSider>
              <div class="logo">
                <Resource
                  :src="logoUrl"
                  class="w-[66px] h-[32px] transform cursor-pointer object-contain"
                  alt="logo"
                  @click="goLink"
                />
                <!-- <XLevelTag class="ml-[10px] tags" /> -->
              </div>
            </XSider>
          </div>
        </div>
        <div
          :key="userId"
          class="content"
          :style="{
            backgroundImage: `url(${transformImage(getFeUrl(`/youyan-platform/png/white.png`))})`,
            ...contentStyle,
          }"
          @scroll="scrolling"
        >
          <ClientOnly>
            <Compatibility />
          </ClientOnly>
          <slot></slot>
        </div>
      </div>
      <!-- <div v-if="!isLoginPage" ref="topRef"> -->
      <!-- <XTop></XTop> -->
      <!-- </div> -->
    </div>
    <Account />
    <VideoDownload />
    <ClientOnly>
      <TemplateModal />
    </ClientOnly>
    <PublishProcess />
    <TasteAccountHint v-model:open="showCreateModal" :accounts="accountList" />
    <FloatFeatures class="pc-only" />
  </ThemeProvider>
</template>

<script lang="ts" setup>
import ThemeProvider from '@/design/widgets/theme-provider/index.vue';
import Compatibility from '~/components/compatibility/index.vue';
import { PageKey } from '~/constants/page';
import { useDetector } from '~/hooks/useDetector';

import Account from '@/features/account/index.vue';
import PublishProcess from '@/features/publish/index.vue';
import VideoDownload from '@/features/videoDownload/index.vue';
import FloatFeatures from '@rendora/common/features/float-features/index.vue';
import TasteAccountHint from '~/features/tasteAccountHint/index.vue';
import TemplateModal from '~/features/template/components/modal.vue';

import { GENERIC_ACTION_KEYS } from '@rendora/common/entities/app_cfg';
import defaultLogo from '~/assets/svg/logo.svg?url';
import { transformImage } from '~/utils/';
import { getFeUrl } from '~/utils/url';

const route = useRoute();
const router = useRouter();
const userInfo = useUserInfo();
const shareVisible = useShare();
const { isHome } = useDetector();
const showCreateModal = useCheckCreate();
const accountList = useAccountList();
const topRef = ref();

const topHeight = ref(0);

useResizeObserver(topRef, entries => {
  const entry = entries[0];
  const { height } = entry.contentRect;
  topHeight.value = height + 65;
});

const routeType = computed(() => route.fullPath.split('/')[1]);
const isLoginPage = computed(() => route.name == PageKey.LOGIN);

const userId = computed(() => userInfo.value?.user_id);

const siderClass = computed(() => {
  return getEnv('area') === 'CN' ? 'w-[240px]' : 'w-[258px]';
});

const contentStyle = computed(() => {
  if (userId.value) {
    return {
      width: `calc(100vw - ${getEnv('area') === 'CN' ? 240 : 258}px)`,
    };
  }
  return {
    width: '100vw',
  };
});

const checkDataPermission = () => {
  const routeList = router.getRoutes();
  const currentRoute = route.name;
  const targetRouter = routeList.find((item: any) => item.name === currentRoute);
  const result = checkRoutePermission(targetRouter);

  if (!result) {
    router.push({
      name: PageKey.CENTER_ACCOUNT,
    });
  }
};

const scrolling = () => {
  if (!shareVisible.value) {
    return;
  }

  shareVisible.value = false;
};
// 个人中心页面退出登录跳转到首页精选
watch(
  () => userInfo.value?.user_id,
  (current, before) => {
    if (routeType.value === 'center') {
      if (current) {
        checkDataPermission();
      } else {
        goHome();
      }
      return;
    }

    // home内容若用户退出登录需定位到首页
    if (current !== before && !current && isHome.value) {
      goHome();
    }
  },
  {
    immediate: true,
  },
);

const permissionData = usePermissionData();
const { hasActionPermission } = usePageAndActionPermissionData();
const model = useCustomSetting();
const logoUrl = computed(() => {
  if (permissionData.value?.config?.logo_url) {
    return transformImage(permissionData.value?.config.logo_url);
  }

  if (userInfo.value?.platform_logo_url) {
    return transformImage(userInfo.value?.platform_logo_url);
  }

  return !isChinese()
    ? getFeUrl('/rendora/logo.svg')
    : (userInfo.value?.user_id && model.value?.logo.url) || defaultLogo;
});

const goLink = () => {
  if (!hasActionPermission(GENERIC_ACTION_KEYS.CLICK_LOGO_REDIRECT)) {
    return;
  }

  trackEvent(TRACK_EVENT.CLICK_LOGO);
  window.location.href = permissionData.value?.config?.logo_click_url
    ? permissionData.value.config.logo_click_url
    : window.location.origin;
};
</script>

<style lang="scss" scoped>
.no-before::before {
  display: none;
}

.wrapper {
  @apply bg-back-bg absolute w-full;

  .sider {
    background-color: var(--menu-bg);
    flex-shrink: 0;
  }

  .content {
    @apply flex flex-col w-full;
    height: 100%;
    border-left: 1px solid var(--back-2);
    background: var(--back-bg);

    :deep(> :last-child) {
      @apply flex flex-col;
      flex: 1;
      overflow: auto;
    }
  }
}

.logo {
  @apply flex items-center text-center;
  font-weight: bold;
  margin-top: 24px;
  padding-left: 48px;
}
</style>
